import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mui, MuiIcons } from '@osu/react-ui';
import useRoleAuthentication from '../../Authentication/hooks/useRoleAuthentication';
import useAdminPermissions from '../hooks/useAdminPermissions';

import { useAdminContext } from '../context/AdminContext';
import StudentSearchInterface from './StudentSearchInterface';
import AdminViewHeader from './AdminViewHeader';
import AdminStudentDashboardWrapper from './AdminStudentDashboardWrapper';

/**
 * AdminDashboardContainer Component
 * 
 * Main container component that orchestrates the admin dashboard view experience.
 * Handles admin role verification, student selection state management, and navigation coordination.
 * 
 * Requirements addressed:
 * - 1.1: Admin role authentication and verification
 * - 5.1: Role-based access control for admin dashboard
 * - 5.2: Proper access denial and redirects for unauthorized users
 * 
 * @returns {JSX.Element} AdminDashboardContainer component
 */
function AdminDashboardContainer() {
  // Navigation and routing
  const navigate = useNavigate();

  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showStudentDashboard, setShowStudentDashboard] = useState(false);

  // Search state management - moved from StudentSearchInterface to persist across navigation
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  // Authentication and permissions
  const {
    isLoggedIn,
    isAnyAdmin,
    user,
    canAccessAdminDashboard,
    canViewAnyStudentDashboard,
    isLoading: authLoading
  } = useRoleAuthentication({
    redirectOnUnauthorized: true
  });

  // Admin permissions
  const {
    requiresStudentViewAccess,
    adminUser
  } = useAdminPermissions();

  // Admin context
  const {
    isAdminView,
    selectedStudent,
    selectStudent,
    clearSelectedStudent,
    enterAdminView,
    exitAdminView
  } = useAdminContext();

  // Verify admin permissions and enter admin view on component mount
  useEffect(() => {
    if (!authLoading && isLoggedIn) {
      if (!canAccessAdminDashboard) {
        setError('You do not have permission to access the admin dashboard.');
        navigate('/dashboard', { replace: true });
        return;
      }

      // Enter admin view mode when component mounts
      if (!isAdminView) {
        enterAdminView();
      }
    }
  }, [
    authLoading,
    isLoggedIn,
    canAccessAdminDashboard,
    isAdminView,
    enterAdminView,
    navigate
  ]);

  // Handle student selection from search interface
  const handleStudentSelect = useCallback((studentId, studentInfo) => {
    try {
      // Handle clearing selection (when studentId is null)
      if (studentId === null || studentInfo === null) {
        clearSelectedStudent();
        setShowStudentDashboard(false);
        setError(null);
        return;
      }

      // Verify student view permissions
      if (!canViewAnyStudentDashboard) {
        throw new Error('You do not have permission to view student dashboards.');
      }

      setIsLoading(true);
      setError(null);

      // Update selected student in admin context
      selectStudent(studentInfo);

      // Show student dashboard view
      setShowStudentDashboard(true);

      // Log admin access for audit purposes (production logging)
      if (process.env.NODE_ENV === 'development') {
        console.log('Admin access:', {
          adminUser: user.name || user.osuid,
          selectedStudent: studentId,
          timestamp: new Date().toISOString(),
          action: 'VIEW_STUDENT_DASHBOARD'
        });
      }

    } catch (error) {
      console.error('Student selection failed:', error);
      setError('You do not have permission to view this student\'s dashboard.');
    } finally {
      setIsLoading(false);
    }
  }, [requiresStudentViewAccess, selectStudent, adminUser]);

  // Handle search state updates from StudentSearchInterface
  const handleSearchStateChange = useCallback((newSearchQuery, newSearchResults, newHasSearched) => {
    setSearchQuery(newSearchQuery);
    setSearchResults(newSearchResults);
    setHasSearched(newHasSearched);
  }, []);

  // Handle exiting admin view
  const handleExitAdminView = useCallback(() => {
    // Clear selected student and exit admin mode
    clearSelectedStudent();
    exitAdminView();
    setShowStudentDashboard(false);
    setError(null);

    // Navigate back to regular dashboard or home
    navigate('/dashboard', { replace: true });
  }, [clearSelectedStudent, exitAdminView, navigate]);

  const handleNavigateToSearch = useCallback(() => {
    setShowStudentDashboard(false);
    // Clear selected student for clean slate (Option 1: Always Go Direct)
    clearSelectedStudent();
    setError(null);
  }, [clearSelectedStudent]);

  // Show loading state during authentication
  if (authLoading) {
    return (
      <Mui.Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}
      >
        <Mui.CircularProgress />
        <Mui.Typography variant="body1" sx={{ ml: 2 }}>
          Verifying admin permissions...
        </Mui.Typography>
      </Mui.Box>
    );
  }

  // Don't render if not authenticated or not admin
  if (!isLoggedIn || !isAnyAdmin || !canAccessAdminDashboard) {
    return null; // Will redirect via useRoleAuthentication
  }

  return (
    <Mui.Box>
      {/* Admin View Header - Only show when viewing student dashboard */}
      {showStudentDashboard && (
        <AdminViewHeader
          selectedStudent={selectedStudent}
          isAdminMode={isAdminView}
        />
      )}

      <Mui.Box sx={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        {/* Error Display */}
        {error && (
          <Mui.Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
          >
            {error}
          </Mui.Alert>
        )}

        {/* Main Content */}
        {!showStudentDashboard ? (
          // Student Search View
          <Mui.Box>
            {/* Page Header */}
            <Mui.Box sx={{ mb: 4 }}>
              <Mui.Typography variant="h4" sx={{
                color: "primary.main",
                mb: 1
              }} gutterBottom>
                Welcome, {user.name || 'Administrator'}.
              </Mui.Typography>
              <Mui.Typography variant="body1" color="text.secondary">
                Search for and select a student to view their dashboard in read-only mode.
              </Mui.Typography>
            </Mui.Box>

            {/* Navigation Breadcrumbs */}
            <Mui.Breadcrumbs sx={{ mb: 3 }}>
              <Mui.Typography color="text.primary">Admin Dashboard</Mui.Typography>
              <Mui.Typography color="text.secondary">Student Search</Mui.Typography>
            </Mui.Breadcrumbs>

            {/* Student Search Interface */}
            <Mui.Paper elevation={1} sx={{ p: 3 }}>
              <StudentSearchInterface
                onStudentSelect={handleStudentSelect}
                selectedStudent={selectedStudent}
                isLoading={isLoading}
                // Pass search state as props to persist across navigation
                searchQuery={searchQuery}
                searchResults={searchResults}
                hasSearched={hasSearched}
                onSearchStateChange={handleSearchStateChange}
              />


            </Mui.Paper>


          </Mui.Box>
        ) : (
          // Student Dashboard View (placeholder for future implementation)
          <Mui.Box>
            {/* Navigation */}
            <Mui.Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Mui.Button
                variant="outlined"
                size="small"
                onClick={handleNavigateToSearch}
                startIcon={<MuiIcons.ArrowBack />}
              >
                Back to Search
              </Mui.Button>

              <Mui.Breadcrumbs>
                <Mui.Link
                  component="button"
                  variant="body2"
                  onClick={handleNavigateToSearch}
                  sx={{ cursor: 'pointer' }}
                >
                  Admin Dashboard
                </Mui.Link>
                <Mui.Typography variant="body2" color="text.secondary">
                  {selectedStudent?.name}
                </Mui.Typography>
              </Mui.Breadcrumbs>
            </Mui.Box>

            {/* AdminStudentDashboardWrapper */}
            <AdminStudentDashboardWrapper
              studentId={selectedStudent.id}
              studentInfo={selectedStudent}
            />
          </Mui.Box>
        )}
      </Mui.Box>
    </Mui.Box>
  );
}

export default AdminDashboardContainer;